import React, { useState, useEffect } from 'react';
import { Ic, ICONS } from '../data/icons';
import { Card, CardHeader, Avatar, Badge, Modal, toast, TableSkeleton, EmptyState } from '../components/shared';
import { fetchPendingUsers, fetchAllUsersAdmin, approveUser, rejectUser, deleteUserAdmin, changeUserRole } from '../api/client';

const ROLES = ['Yönetici','Müdahale','Editör','Okuyucu'];
const ROLE_COLORS = { 'Süper Admin':'#ef4444', 'Yönetici':'#f59e0b', 'Müdahale':'#a78bfa', 'Editör':'#3b82f6', 'Okuyucu':'#22c55e' };
const STATUS_COLORS = { approved:'#22c55e', pending:'#f59e0b', rejected:'#ef4444' };
const STATUS_LABELS = { approved:'Onaylı', pending:'Bekliyor', rejected:'Reddedildi' };

export default function AdminPanel({ C, currentUser }) {
  const [tab, setTab]             = useState('pending');
  const [pending, setPending]     = useState([]);
  const [allUsers, setAllUsers]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [approveModal, setApproveModal] = useState(null); // { user, role }
  const [rejectModal, setRejectModal]   = useState(null);
  const [deleteModal, setDeleteModal]   = useState(null);
  const [roleModal, setRoleModal]       = useState(null); // { user, role }

  const loadData = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([fetchPendingUsers(), fetchAllUsersAdmin()]);
      setPending(p?.items || []);
      setAllUsers(a?.items || []);
    } catch (err) {
      toast.error('Veriler yüklenemedi: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleApprove = async () => {
    if (!approveModal) return;
    try {
      await approveUser(approveModal.user.id, approveModal.role);
      toast.success(`${approveModal.user.name} onaylandı (${approveModal.role})`);
      await loadData();
    } catch (err) { toast.error(err.message); }
    setApproveModal(null);
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    try {
      await rejectUser(rejectModal.id);
      toast.success(`${rejectModal.name} reddedildi`);
      await loadData();
    } catch (err) { toast.error(err.message); }
    setRejectModal(null);
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      await deleteUserAdmin(deleteModal.id);
      toast.success(`${deleteModal.name} silindi`);
      await loadData();
    } catch (err) { toast.error(err.message); }
    setDeleteModal(null);
  };

  const handleRoleChange = async () => {
    if (!roleModal) return;
    try {
      await changeUserRole(roleModal.user.id, roleModal.role);
      toast.success(`${roleModal.user.name} rolü → ${roleModal.role}`);
      await loadData();
    } catch (err) { toast.error(err.message); }
    setRoleModal(null);
  };

  const approved  = allUsers.filter(u => u.status === 'approved');
  const rejected  = allUsers.filter(u => u.status === 'rejected');

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

      {/* Üst banner */}
      <div style={{ background:'linear-gradient(135deg,rgba(59,130,246,0.12),rgba(139,92,246,0.12))', border:`1px solid rgba(59,130,246,0.2)`, borderRadius:14, padding:'16px 20px', display:'flex', alignItems:'center', gap:14 }}>
        <div style={{ width:46, height:46, borderRadius:12, background:'linear-gradient(135deg,#3b82f6,#8b5cf6)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <Ic d={ICONS.shield} size={22} s='#fff' />
        </div>
        <div>
          <div style={{ fontSize:15, fontWeight:800, color:C.text }}>Sistem Yönetici Paneli</div>
          <div style={{ fontSize:12.5, color:C.textSub, marginTop:2 }}>
            Hoş geldiniz, <strong style={{ color:C.text }}>{currentUser?.name}</strong> · Sistem Sahibi
          </div>
        </div>
        <div style={{ marginLeft:'auto', display:'flex', gap:10 }}>
          {pending.length > 0 && (
            <div style={{ background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:10, padding:'8px 14px', textAlign:'center' }}>
              <div style={{ fontSize:22, fontWeight:800, color:'#fbbf24', lineHeight:1 }}>{pending.length}</div>
              <div style={{ fontSize:10.5, color:'#fde68a', marginTop:2 }}>Onay bekliyor</div>
            </div>
          )}
          <div style={{ background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:10, padding:'8px 14px', textAlign:'center' }}>
            <div style={{ fontSize:22, fontWeight:800, color:'#22c55e', lineHeight:1 }}>{approved.length}</div>
            <div style={{ fontSize:10.5, color:'#86efac', marginTop:2 }}>Aktif kullanıcı</div>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display:'flex', gap:6 }}>
        {[
          { k:'pending',  l:'Onay Bekleyenler', cnt:pending.length, color:'#f59e0b' },
          { k:'approved', l:'Onaylı Kullanıcılar', cnt:approved.length, color:'#22c55e' },
          { k:'rejected', l:'Reddedilenler', cnt:rejected.length, color:'#ef4444' },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 16px', borderRadius:9, border:`1px solid ${tab===t.k ? t.color+'44' : C.border}`, background:tab===t.k ? `${t.color}14` : 'transparent', color:tab===t.k ? t.color : C.textSub, fontSize:13, fontWeight:tab===t.k?700:400, cursor:'pointer', transition:'all 0.15s' }}>
            {t.l}
            <span style={{ background:tab===t.k ? `${t.color}30` : C.surface2, color:tab===t.k ? t.color : C.textFaint, fontSize:11, fontWeight:700, padding:'1px 7px', borderRadius:99 }}>{t.cnt}</span>
          </button>
        ))}
      </div>

      {/* Onay bekleyenler */}
      {tab === 'pending' && (
        <Card C={C}>
          <CardHeader C={C} title='Onay Bekleyen Kayıtlar' action={
            <button onClick={loadData} style={{ display:'flex', alignItems:'center', gap:5, padding:'5px 12px', borderRadius:7, border:`1px solid ${C.border}`, background:'transparent', color:C.textSub, fontSize:12, cursor:'pointer', fontWeight:600 }}>
              <Ic d={ICONS.refresh} size={13} /> Yenile
            </button>
          } />
          {loading ? <TableSkeleton rows={4} cols={5} C={C} /> : pending.length === 0 ? (
            <EmptyState icon='check' title='Bekleyen kayıt yok' desc='Tüm kayıtlar işlendi' C={C} />
          ) : pending.map(u => (
            <div key={u.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 20px', borderTop:`1px solid ${C.border}`, flexWrap:'wrap' }}>
              <Avatar initials={u.name.split(' ').map(s=>s[0]).join('').slice(0,2).toUpperCase()} size={38} />
              <div style={{ flex:1, minWidth:120 }}>
                <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{u.name}</div>
                <div style={{ fontSize:12, color:C.textFaint, marginTop:2 }}>{u.email} · {u.dept}</div>
                {u.phone && <div style={{ fontSize:11, color:C.textFaint }}>{u.phone}</div>}
              </div>
              <div style={{ fontSize:11, color:C.textFaint }}>
                {new Date(u.created_at).toLocaleDateString('tr-TR',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => setApproveModal({ user:u, role:'Okuyucu' })} style={{ padding:'7px 16px', borderRadius:9, border:'1px solid rgba(34,197,94,0.4)', background:'rgba(34,197,94,0.12)', color:'#22c55e', fontSize:12.5, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                  <Ic d={ICONS.check} size={13} s='#22c55e' /> Onayla
                </button>
                <button onClick={() => setRejectModal(u)} style={{ padding:'7px 14px', borderRadius:9, border:'1px solid rgba(239,68,68,0.4)', background:'rgba(239,68,68,0.1)', color:'#ef4444', fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
                  Reddet
                </button>
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Onaylı kullanıcılar */}
      {tab === 'approved' && (
        <Card C={C}>
          <CardHeader C={C} title='Onaylı Kullanıcılar' action={<span style={{ fontSize:11, color:C.textFaint }}>{approved.length} kullanıcı</span>} />
          {loading ? <TableSkeleton rows={5} cols={5} C={C} /> : approved.length === 0 ? (
            <EmptyState icon='users' title='Henüz onaylı kullanıcı yok' C={C} />
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:600 }}>
                <thead>
                  <tr style={{ background:C.surface2 }}>
                    {['Kullanıcı','E-posta','Rol','Departman','Son Aktif','İşlem'].map(h => (
                      <th key={h} style={{ textAlign:'left', padding:'9px 16px', fontSize:10.5, fontWeight:700, color:C.textFaint, letterSpacing:'0.7px', textTransform:'uppercase', borderBottom:`1px solid ${C.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {approved.map(u => (
                    <tr key={u.id} onMouseEnter={e=>e.currentTarget.style.background=C.surface2} onMouseLeave={e=>e.currentTarget.style.background='transparent'} style={{ transition:'background 0.1s' }}>
                      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.border}` }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <Avatar initials={u.name.split(' ').map(s=>s[0]).join('').slice(0,2).toUpperCase()} size={28} />
                          <div>
                            <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{u.name}</div>
                            {u.is_system_owner === 1 && <div style={{ fontSize:10, color:'#f59e0b' }}>👑 Sistem Sahibi</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.border}` }}><span style={{ fontSize:12, color:C.textSub }}>{u.email}</span></td>
                      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.border}` }}>
                        <Badge color={ROLE_COLORS[u.role]||C.accent}>{u.role}</Badge>
                      </td>
                      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.border}` }}><span style={{ fontSize:12, color:C.textSub }}>{u.dept}</span></td>
                      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.border}` }}><span style={{ fontSize:11, color:C.textFaint }}>{u.last_active ? new Date(u.last_active).toLocaleDateString('tr-TR') : '-'}</span></td>
                      <td style={{ padding:'11px 16px', borderBottom:`1px solid ${C.border}` }}>
                        {u.is_system_owner ? (
                          <span style={{ fontSize:11, color:C.textFaint }}>—</span>
                        ) : (
                          <div style={{ display:'flex', gap:6 }}>
                            <button onClick={() => setRoleModal({ user:u, role:u.role })} style={{ padding:'4px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:'transparent', color:C.textSub, fontSize:11, fontWeight:600, cursor:'pointer' }}>Rol</button>
                            <button onClick={() => setDeleteModal(u)} style={{ padding:'4px 10px', borderRadius:7, border:'1px solid rgba(239,68,68,0.4)', background:'rgba(239,68,68,0.08)', color:'#ef4444', fontSize:11, fontWeight:600, cursor:'pointer' }}>Sil</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Reddedilenler */}
      {tab === 'rejected' && (
        <Card C={C}>
          <CardHeader C={C} title='Reddedilen Kayıtlar' action={<span style={{ fontSize:11, color:C.textFaint }}>{rejected.length} kayıt</span>} />
          {loading ? <TableSkeleton rows={3} cols={4} C={C} /> : rejected.length === 0 ? (
            <EmptyState icon='check' title='Reddedilen kayıt yok' C={C} />
          ) : rejected.map(u => (
            <div key={u.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'13px 20px', borderTop:`1px solid ${C.border}`, flexWrap:'wrap' }}>
              <Avatar initials={u.name.split(' ').map(s=>s[0]).join('').slice(0,2).toUpperCase()} size={34} />
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13.5, fontWeight:600, color:C.text }}>{u.name}</div>
                <div style={{ fontSize:12, color:C.textFaint }}>{u.email} · {u.dept}</div>
              </div>
              <Badge color='#ef4444'>Reddedildi</Badge>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => setApproveModal({ user:u, role:'Okuyucu' })} style={{ padding:'6px 14px', borderRadius:8, border:'1px solid rgba(34,197,94,0.4)', background:'rgba(34,197,94,0.1)', color:'#22c55e', fontSize:12, fontWeight:600, cursor:'pointer' }}>Geri Al & Onayla</button>
                <button onClick={() => setDeleteModal(u)} style={{ padding:'6px 10px', borderRadius:8, border:'1px solid rgba(239,68,68,0.3)', background:'rgba(239,68,68,0.08)', color:'#ef4444', fontSize:12, cursor:'pointer' }}>Sil</button>
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Onay Modalı */}
      <Modal isOpen={!!approveModal} onClose={() => setApproveModal(null)} onConfirm={handleApprove}
        title='Kullanıcıyı Onayla' type='success' C={C} confirmLabel='Onayla ve Aktifleştir' wide>
        {approveModal && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:C.surface2, borderRadius:10 }}>
              <Avatar initials={approveModal.user.name.split(' ').map(s=>s[0]).join('').slice(0,2).toUpperCase()} size={38} />
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{approveModal.user.name}</div>
                <div style={{ fontSize:12, color:C.textFaint }}>{approveModal.user.email}</div>
              </div>
            </div>
            <div>
              <label style={{ fontSize:11.5, fontWeight:600, color:C.textFaint, display:'block', marginBottom:8 }}>Atanacak Rol</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:7 }}>
                {ROLES.map(r => (
                  <button key={r} onClick={() => setApproveModal(m => ({...m, role:r}))} style={{ padding:'9px 10px', borderRadius:9, border:`1px solid ${approveModal.role===r ? (ROLE_COLORS[r]+'44') : C.border}`, background:approveModal.role===r ? `${ROLE_COLORS[r]}18` : 'transparent', color:approveModal.role===r ? ROLE_COLORS[r] : C.textSub, fontSize:12.5, fontWeight:approveModal.role===r?700:400, cursor:'pointer', transition:'all 0.15s' }}>
                    {r}
                  </button>
                ))}
              </div>
              <div style={{ marginTop:10, padding:'10px 12px', background:C.surface2, borderRadius:8, fontSize:12, color:C.textFaint }}>
                {{
                  'Yönetici':'Cihaz ve politika yönetimi yapabilir',
                  'Müdahale':'Uzaktan komut gönderebilir',
                  'Editör':'Kayıtları düzenleyebilir',
                  'Okuyucu':'Sadece görüntüleyebilir',
                }[approveModal.role]}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Reddet Modal */}
      <Modal isOpen={!!rejectModal} onClose={() => setRejectModal(null)} onConfirm={handleReject}
        title='Kaydı Reddet'
        message={`"${rejectModal?.name}" adlı kullanıcının kaydını reddetmek istediğinizden emin misiniz? Kullanıcı sisteme giriş yapamayacak.`}
        type='danger' C={C} confirmLabel='Reddet' />

      {/* Sil Modal */}
      <Modal isOpen={!!deleteModal} onClose={() => setDeleteModal(null)} onConfirm={handleDelete}
        title='Kullanıcıyı Kalıcı Sil'
        message={`"${deleteModal?.name}" adlı kullanıcı veritabanından tamamen silinecek. Bu işlem geri alınamaz.`}
        type='danger' C={C} confirmLabel='Kalıcı Sil' />

      {/* Rol Değiştir Modal */}
      <Modal isOpen={!!roleModal} onClose={() => setRoleModal(null)} onConfirm={handleRoleChange}
        title='Kullanıcı Rolünü Değiştir' type='info' C={C} confirmLabel='Rolü Güncelle' wide>
        {roleModal && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ fontSize:13, color:C.textSub, marginBottom:4 }}><strong style={{ color:C.text }}>{roleModal.user.name}</strong> için yeni rol seçin:</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:7 }}>
              {ROLES.map(r => (
                <button key={r} onClick={() => setRoleModal(m => ({...m, role:r}))} style={{ padding:'10px', borderRadius:9, border:`1px solid ${roleModal.role===r ? (ROLE_COLORS[r]+'44') : C.border}`, background:roleModal.role===r ? `${ROLE_COLORS[r]}18` : 'transparent', color:roleModal.role===r ? ROLE_COLORS[r] : C.textSub, fontSize:13, fontWeight:roleModal.role===r?700:400, cursor:'pointer', transition:'all 0.15s' }}>
                  {r}
                </button>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

